import React, { useState, useEffect, useMemo } from 'react';
import { MessageCircle, Phone, Send, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { ordersAPI, authFetch } from '@/lib/api';
import { formatDate } from '@/lib/utils';

/**
 * WhatsAppShareDialog
 * --------------------
 * "Click-to-chat" sharing. We never push the message through a WhatsApp Business
 * API — instead we:
 *  1. Let the user pick a supplier contact (or type a custom number).
 *  2. POST to a share endpoint (single order OR consolidation) which logs the
 *     action and crafts a pre-filled French message + a normalized wa.me URL.
 *  3. Open the wa.me URL in a new tab (WhatsApp Web / phone) AND trigger the
 *     PDF download so the user can attach it manually in the chat.
 *
 * The supplier `phone` field is reused as the WhatsApp number (no separate
 * field needed — most pro suppliers have a single mobile number anyway).
 *
 * Props:
 *  - order: { id, whatsapp_sent_at?, whatsapp_logs? } OR consolidation-shaped
 *    object { id?: optional, whatsapp_sent_at?, whatsapp_logs? } — only used
 *    for the "déjà partagée" banner (gracefully handles missing fields).
 *  - supplier: supplier object (contacts array)
 *  - shareFn: async ({phone, contact_name}) => {data: {wa_url, order_number, is_test?, original_phone?}}
 *      Defaults to the single-order ordersAPI.sendWhatsApp(order.id, ...).
 *  - pdfFilename: filename for the downloaded PDF (defaults to `commande_${order_number}.pdf`).
 *  - pdfUrl: direct PDF url to trigger download (defaults to ordersAPI.getPdfUrl(order.id)).
 *  - shareLabel: optional label used in the toast (e.g. "consolidée" vs "")
 */

const normalizeForDisplay = (raw) => {
    if (!raw) return '';
    const digits = String(raw).replace(/\D/g, '');
    if (!digits) return '';
    // Pretty-print FR mobile numbers: 06 96 12 34 56 / +33 6 96 12 34 56
    if (digits.length === 10 && digits.startsWith('0')) {
        return digits.match(/.{1,2}/g).join(' ');
    }
    return raw;
};

const WhatsAppShareDialog = ({ open, onOpenChange, order, supplier, onShared, shareFn, pdfUrl, pdfFilename, shareLabel }) => {
    const [selectedPhone, setSelectedPhone] = useState('');
    const [selectedContactName, setSelectedContactName] = useState('');
    const [customPhone, setCustomPhone] = useState('');
    const [loading, setLoading] = useState(false);
    const [pdfFile, setPdfFile] = useState(null);

    // Build the list of WhatsApp-able contacts: ONLY those flagged
    // `send_whatsapp = true` on the supplier. We deliberately do NOT include
    // the supplier-level phone here — the user must explicitly opt-in each
    // contact in the Fournisseurs page. This prevents accidental sharing to
    // generic supplier switchboards. De-dup by phone.
    const contactOptions = useMemo(() => {
        if (!supplier) return [];
        const opts = [];
        const seen = new Set();
        for (const c of (supplier.contacts || [])) {
            if (!c.send_whatsapp) continue;
            const digits = (c.phone || '').replace(/\D/g, '');
            if (!digits || digits.length < 8) continue;
            if (seen.has(digits)) continue;
            seen.add(digits);
            opts.push({ name: c.name || supplier.name, phone: c.phone, title: c.title });
        }
        return opts;
    }, [supplier]);

    // Reset state every time the dialog opens with a new order.
    useEffect(() => {
        if (open) {
            setCustomPhone('');
            if (contactOptions.length > 0) {
                setSelectedPhone(contactOptions[0].phone);
                setSelectedContactName(contactOptions[0].name);
            } else {
                setSelectedPhone('');
                setSelectedContactName('');
            }
        }
    }, [open, contactOptions]);

    // Préchargement du PDF à l'ouverture : indispensable pour que le partage
    // natif (navigator.share) reste dans la fenêtre du geste utilisateur.
    useEffect(() => {
        if (!open) { setPdfFile(null); return; }
        const downloadUrl = pdfUrl || (order?.id ? ordersAPI.getPdfUrl(order.id) : null);
        if (!downloadUrl) return;
        let cancelled = false;
        (async () => {
            try {
                const resp = await authFetch(downloadUrl);
                if (!resp.ok) return;
                const blob = await resp.blob();
                if (cancelled) return;
                const name = pdfFilename || `commande_${order?.order_number || ''}.pdf`;
                setPdfFile(new File([blob], name, { type: 'application/pdf' }));
            } catch { /* fallback téléchargement classique */ }
        })();
        return () => { cancelled = true; };
    }, [open, pdfUrl, pdfFilename, order?.id, order?.order_number]);

    const effectivePhone = customPhone.trim() || selectedPhone;
    const effectiveContactName = customPhone.trim() ? '' : selectedContactName;

    const handleShare = async () => {
        if (!order && !shareFn) return;
        if (!effectivePhone || effectivePhone.replace(/\D/g, '').length < 8) {
            toast.error('Numéro de téléphone invalide');
            return;
        }
        setLoading(true);
        try {
            // Use the caller-provided share function when given (consolidation
            // case), otherwise fall back to the single-order endpoint.
            const callShare = shareFn
                ? shareFn
                : ({ phone, contact_name }) => ordersAPI.sendWhatsApp(order.id, { phone, contact_name });
            const { data } = await callShare({
                phone: effectivePhone,
                contact_name: effectiveContactName || null,
            });

            const labelSuffix = shareLabel ? ` ${shareLabel}` : '';
            const filename = pdfFilename || `commande_${data.order_number}.pdf`;
            let file = pdfFile;
            if (!file) {
                try {
                    const resp = await authFetch(pdfUrl || ordersAPI.getPdfUrl(order.id));
                    if (resp.ok) file = new File([await resp.blob()], filename, { type: 'application/pdf' });
                } catch { /* ignore */ }
            }

            // 1) Partage natif (mobile) : le PDF est réellement JOINT dans WhatsApp.
            if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
                try {
                    await navigator.share({ files: [file], text: data.message || '' });
                    toast.success(`PDF${labelSuffix} joint — choisissez WhatsApp (${effectiveContactName || effectivePhone}) dans le menu de partage.`, { duration: 6000 });
                    onShared?.();
                    onOpenChange(false);
                    return;
                } catch (shareErr) {
                    if (shareErr?.name === 'AbortError') return; // partage annulé par l'utilisateur
                    // NotAllowedError (geste expiré) ou autre → repli wa.me ci-dessous
                }
            }

            // 2) Repli (desktop) : ouvrir WhatsApp Web avec le message pré-rempli
            //    et télécharger le PDF pour le glisser dans la conversation.
            window.open(data.wa_url, '_blank', 'noopener,noreferrer');
            if (file) {
                const objUrl = URL.createObjectURL(file);
                const link = document.createElement('a');
                link.href = objUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => URL.revokeObjectURL(objUrl), 10000);
            }

            if (data.is_test) {
                toast.success(
                    `Mode test : commande${labelSuffix} redirigée vers le numéro de test (destinataire réel : ${data.original_phone}). PDF téléchargé.`,
                    { duration: 6000 }
                );
            } else {
                toast.success(
                    `WhatsApp ouvert pour ${effectiveContactName || effectivePhone}. PDF${labelSuffix} téléchargé — pensez à le joindre dans la conversation.`
                );
            }
            onShared?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors du partage WhatsApp');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="whatsapp-share-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <MessageCircle className="w-5 h-5 text-emerald-600" />
                        Partager la commande sur WhatsApp
                    </DialogTitle>
                    <DialogDescription>
                        Choisissez un contact ou saisissez un numéro. Sur mobile, le PDF est
                        joint directement dans WhatsApp ; sur ordinateur, il est téléchargé
                        pour être glissé dans la conversation.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    {order?.whatsapp_sent_at && (
                        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                            Déjà partagée le {formatDate(order.whatsapp_sent_at)}
                            {order.whatsapp_logs?.length > 1
                                ? ` (${order.whatsapp_logs.length} envois)`
                                : ''}
                        </div>
                    )}

                    {contactOptions.length > 0 ? (
                        <div>
                            <Label className="text-xs text-slate-600">Contact fournisseur</Label>
                            <div className="mt-1 space-y-1">
                                {contactOptions.map((c, i) => {
                                    const active = !customPhone && selectedPhone === c.phone;
                                    return (
                                        <button
                                            key={`${c.phone}-${i}`}
                                            type="button"
                                            onClick={() => {
                                                setSelectedPhone(c.phone);
                                                setSelectedContactName(c.name);
                                                setCustomPhone('');
                                            }}
                                            className={`w-full text-left px-3 py-2 rounded border transition-colors flex items-center gap-2 ${
                                                active
                                                    ? 'bg-emerald-50 border-emerald-400 ring-1 ring-emerald-300'
                                                    : 'bg-white border-slate-200 hover:bg-slate-50'
                                            }`}
                                            data-testid={`whatsapp-contact-${i}`}
                                        >
                                            <Phone className={`w-4 h-4 ${active ? 'text-emerald-600' : 'text-slate-400'}`} />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-slate-900 truncate">
                                                    {c.name}
                                                    {c.title && (
                                                        <span className="text-xs font-normal text-slate-500 ml-1.5">
                                                            ({c.title})
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-slate-500 font-mono">
                                                    {normalizeForDisplay(c.phone)}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
                            Aucun contact fournisseur n&apos;est autorisé pour WhatsApp.
                            Pour activer un contact, ouvrez la fiche du fournisseur et cochez
                            l&apos;icône <MessageCircle className="w-3 h-3 inline-block align-text-bottom text-emerald-500" />
                            {' '}à côté de son numéro de téléphone.
                        </div>
                    )}

                    <div>
                        <Label htmlFor="whatsapp-custom-phone" className="text-xs text-slate-600">
                            {contactOptions.length > 0 ? 'Ou un autre numéro' : 'Numéro de téléphone'}
                        </Label>
                        <Input
                            id="whatsapp-custom-phone"
                            value={customPhone}
                            onChange={(e) => setCustomPhone(e.target.value)}
                            placeholder="06 96 12 34 56 ou +596 ..."
                            className="mt-1 h-9 text-sm"
                            data-testid="whatsapp-custom-phone"
                        />
                        <p className="mt-1 text-[11px] text-slate-400">
                            Les numéros commençant par 0 sont préfixés en +33 (France métropole).
                            Pour la Martinique ou un autre indicatif, saisissez +596 / +590 / +1...
                        </p>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={loading}
                        data-testid="whatsapp-cancel-btn"
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleShare}
                        disabled={loading || !effectivePhone}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="whatsapp-share-confirm-btn"
                    >
                        {loading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Send className="w-4 h-4 mr-2" />
                        )}
                        Ouvrir WhatsApp
                        <FileText className="w-3.5 h-3.5 ml-2 opacity-70" />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default WhatsAppShareDialog;
